/**
 * The people the tenant knows, from `/api/people-mapping/people`.
 *
 * That endpoint is the identity layer the recorder writes into: one person per
 * human, with every address and alias they have appeared under folded onto it.
 * It is the right source for a picker precisely because it has already done the
 * merging — a directory built from meeting rosters would offer the same
 * colleague three times under three spellings.
 *
 * `person_type` is what decides internal from external, and it is a property of
 * the *person* rather than of their email domain: a contractor on a client
 * address can be internal, and someone at the firm's own domain can be a
 * customer. Guessing from the domain here would disagree with the access rules
 * the server applies.
 */
import { apiGet } from '@/lib/api'
import type { Person } from './types'

interface WirePerson {
  id: string
  display_name: string | null
  person_type: string
  emails?: Array<{ email: string; is_primary?: boolean }>
}

function primaryEmail(wire: WirePerson): string {
  const emails = wire.emails ?? []
  return (emails.find((entry) => entry.is_primary) ?? emails[0])?.email?.toLowerCase() ?? ''
}

/**
 * The last directory the server sent.
 *
 * Read synchronously by pickers that resolve an address while rendering. Empty
 * until `listPeople` has run, which is the same bargain as `spacesSync` — a row
 * then shows the raw address, which is worse than a name and better than a gap.
 */
let CACHE: Person[] = []

export async function listPeople(): Promise<Person[]> {
  const { people } = await apiGet<{ people: WirePerson[] }>('/api/people-mapping/people?limit=500')
  CACHE = people
    .map((wire) => ({
      email: primaryEmail(wire),
      name: wire.display_name || primaryEmail(wire),
      isInternal: wire.person_type === 'internal',
    }))
    .filter((person) => person.email)
    .sort((left, right) => left.name.localeCompare(right.name))
  return CACHE
}

export function peopleSync(): Person[] {
  return CACHE
}

export function findPerson(email: string): Person | undefined {
  const normalized = email.toLowerCase()
  return CACHE.find((person) => person.email === normalized)
}

/**
 * The signed-in person.
 *
 * Set from the session rather than looked up, because the answer has to be
 * right before any request has been made — the chat timeline draws "you" on the
 * first render.
 */
let current: Person & { imageUrl?: string | null } = { email: '', name: '', isInternal: true }

export function setCurrentPerson(person: Person & { imageUrl?: string | null }): void {
  current = person
}

export function currentPerson(): Person & { imageUrl?: string | null } {
  return current
}

/**
 * Languages a share notification can go out in.
 *
 * Granting access emails the recipient, so the language is chosen at grant
 * time rather than inferred — German first, matching the primary market.
 */
export const LANGUAGES = [
  { code: 'de', label: 'Deutsch' },
  { code: 'en', label: 'English' },
] as const

export const DEFAULT_LANGUAGE = 'de'
